<template>
  <CContainer
    class="datatable-container contact-tracing-view"
    @click="onContainerClick"
  >
    <Loader :is-processing="isLoading" />
    <CRow class="mt-4">
      <CCol md="12" class="mb-4 mb-lg-5">
        <h1 class="text-center">
          Meet-up Report
          <HelpCenterButton
            classes="me-2"
            content-key="contact_tracing_meet_up_report"
          />
        </h1>
      </CCol>
      <CCol md="4" lg="3">
        <LazyLoadSelect
          :classes="[{ 'is-invalid': reqResponse.errors['user_id'] }, 'mb-3']"
          :selected="filterParams.by_student"
          type="student"
          placeholder="Student"
          @changed="
            (value) => {
              filterParams.by_student = value
            }
          "
        >
          <template #footer>
            <div v-if="reqResponse.errors['user_id']" class="invalid-feedback">
              {{ reqResponse.errors["user_id"][0] }}
            </div>
          </template>
        </LazyLoadSelect>
      </CCol>
      <CCol md="4" lg="3">
        <DatePicker
          v-model="filterParams.dates"
          class="cs-date-picker"
          :masks="{ input: 'MM/DD/YYYY' }"
          placeholder="Year"
          mode="range"
          is-range
          :max-date="maxDate"
          :min-date="minDate"
          @drag="setRangeValidation"
          @popover-will-show="handleDatePopoverShow"
        >
          <template #default="{ inputValue, togglePopover, hidePopover }">
            <div class="input-icon-field cursor-pointer">
              <CFormInput
                :class="[
                  { 'is-invalid': reqResponse.errors['to_date'] },
                  'date-calendar-option cursor-pointer'
                ]"
                placeholder="Date"
                type="readonly"
                :value="inputValue.start + '-' + inputValue.end"
                readonly
                @click="showCalendarRanges(hidePopover)"
              />
              <span class="field-icon" @click="showCalendarRanges(hidePopover)">
                <i class="fi flaticon-calendar me-1" />
              </span>
              <div
                v-show="calendar.isRangesVisible"
                class="optional-date-ranges-container"
              >
                <div
                  v-for="(range, index) in optionalDateRanges"
                  :key="index"
                  :class="{
                    active: range.label === calendar.activeRange.label
                  }"
                  class="option-range p-2"
                  @click="setCalendarRange(range, togglePopover)"
                >
                  {{ range.label }}
                </div>
              </div>
              <div
                v-if="reqResponse.errors['to_date']"
                class="invalid-feedback d-block"
              >
                {{ reqResponse.errors["to_date"][0] }}
              </div>
            </div>
          </template>
        </DatePicker>
      </CCol>
      <CCol md="4" lg="3" />
      <CCol md="12" class="mt-1 mt-md-4 text-center text-sm-start">
        <div class="d-inline-block text-center" @click="advancedSearch()">
          <CButton class="me-2 btn bg-gradient-blue text-white">
            <div class="p-small">Search</div>
          </CButton>
        </div>
        <div class="d-inline-block text-center">
          <CButton class="ms-2 p-small bg-gradient-blue" @click="clearFilter()">
            <span class="btn bg-white px-4"
              ><b class="text-graident-blue px-2">Clear</b></span
            >
          </CButton>
        </div>
      </CCol>
    </CRow>

    <CRow class="mt-5">
      <CCol md="12">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div
            v-for="(tab, index) in tableTabs"
            :key="index"
            class="table-tab"
            :class="{ active: tab.value === activeTableTab.value }"
          >
            {{ tab.label }}
          </div>
          <div class="actions d-flex align-items-center">
            <CLink
              class="lh-normal text-accent-blue-light ms-2 ms-sm-0"
              download="contact-tracing.csv"
              target="_blank"
              @click="handleContactTracing(pagination.activePage, 'csv')"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </CLink>
            <CLink
              class="mx-1 mx-sm-4 lh-normal text-accent-blue-light"
              download="contact-tracing.xlsx"
              target="_blank"
              @click="handleContactTracing(pagination.activePage, 'excel')"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download Excel</span>
            </CLink>
            <CLink
              class="mx-1 mx-sm-4 lh-normal text-accent-blue-light"
              target="_blank"
              @click="handleContactTracing(pagination.activePage, 'print')"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Print</span>
            </CLink>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search..."
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div
          class="status-guides-container d-flex justify-space-between align-items-center mb-2"
        >
          <div class="d-flex align-items-center">
            <div class="me-2">Optional:</div>
            <div class="status-list flex-wrap flex-md-nowrap">
              <CCheckBox
                label="Student Number"
                name="student_number"
                :inline="true"
                :custom="true"
                class="custom-checkbox m-0"
                @change="updateStudentNumber"
              />
            </div>
          </div>
          <per-page-selector :pagination="pagination" :action="setPerPage" />
        </div>

        <DataTable
          v-if="contactTracings"
          id="contactTracingTable"
          class="datatable bg-white mb-5"
          add-table-classes="contactTracingDataTable"
          :column-filter="true"
          :sorter="{ external: true }"
          :items="contactTracings"
          :fields="activeDataTableFields"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #student_sis_id="{ item }">
            <td>
              <div>{{ item.student_sis_id ? item.student_sis_id : "" }}</div>
            </td>
          </template>
          <template #first_name="{ item }">
            <td>{{ item.first_name }}</td>
          </template>
          <template #first_name-filter>
            <input
              v-model="columnSearchKeys.first_name.value"
              placeholder="Search by first name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #last_name="{ item }">
            <td>{{ item.last_name }}</td>
          </template>
          <template #last_name-filter>
            <input
              v-model="columnSearchKeys.last_name.value"
              placeholder="Search by last name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #email="{ item }">
            <td>{{ item.email }}</td>
          </template>
          <template #email-filter>
            <input
              v-model="columnSearchKeys.email.value"
              placeholder="Search by email"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #overlap_time_count-filter>
            <input
              v-model="columnSearchKeys.overlap_time_count.value"
              placeholder="Search by number"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #share_same_location="{ item }">
            <td>{{ item.share_same_location ? "Yes" : "No" }}</td>
          </template>
        </DataTable>

        <Pagination
          v-if="
            pagination &&
            pagination.pages &&
            pagination.pages > 1 &&
            !isLazyLoadingMode
          "
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
    <iframe
      class="opacity-0"
      name="print_frame"
      width="0"
      height="0"
      frameborder="0"
      src="about:blank"
    />
  </CContainer>
</template>

<script>
import Loader from "@/components/Loader"
import { mapGetters } from "vuex"
import LazyLoadSelect from "@/components/LazyLoadSelect.vue"
import moment from "moment-timezone"
import PerPageSelector from "../PerPageSelector.vue"
import HelpCenterButton from "@/components/HelpCenterButton"
import * as XLSX from "xlsx/xlsx.mjs"

export default {
  name: "ContactTracingDataTable",
  components: {
    Loader,
    LazyLoadSelect,
    PerPageSelector,
    HelpCenterButton
  },
  data() {
    return {
      isLoading: false,
      searchTimeOut: null,
      isLazyLoadingMode: false,
      today: moment().format("YYYY-MM-DD HH:mm:ss"),
      filterParams: {
        search_id: null,
        search_query: "",
        sort_query: "",
        by_student: null,
        dates: {
          start: moment().format("YYYY-MM-DD HH:mm:ss"),
          end: moment().format("YYYY-MM-DD HH:mm:ss")
        }
      },
      showStudentNumber: false,
      dataTableFields: [
        {
          key: "student_sis_id",
          filter: false,
          sorter: false,
          label: "Student number",
          _style: "min-width:150px"
        },
        { key: "last_name", label: "Last name", _style: "width:180px" },
        { key: "first_name", label: "First name", _style: "width:180px" },
        { key: "email", label: "Email", _style: "width:150px" },
        {
          key: "overlap_time_count",
          label: "Number of times passes overlapped",
          _style: "min-width:260px"
        },
        {
          key: "share_same_location",
          label: "Did they share a location?",
          _style: "min-width:210px",
          filter: false
        }
      ],
      columnSearchKeys: {
        globalQuery: "",
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        last_name: { value: "", column: ["last_name"], sortBy: "last_name" },
        first_name: { value: "", column: ["first_name"], sortBy: "first_name" },
        email: { value: "", column: ["email"], sortBy: "email" },
        overlap_time_count: {
          value: "",
          column: ["overlap_time_count"],
          sortBy: "overlap_time_count"
        },
        share_same_location: {
          value: "",
          column: ["share_same_location"],
          sortBy: "share_same_location"
        }
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      tableTabs: [
        {
          label: "Meet-up Report",
          value: "contact-tracing"
        }
      ],
      activeTableTab: {
        value: "contact-tracing"
      },
      calendar: {
        isCalendarVisible: false,
        isRangesVisible: false,
        activeRange: {
          label: "Today",
          range: {
            start: moment().format("YYYY-MM-DD HH:mm:ss"),
            end: moment().format("YYYY-MM-DD HH:mm:ss")
          }
        }
      },
      optionalDateRanges: [
        {
          label: "Today",
          range: {
            start: moment().format("YYYY-MM-DD HH:mm:ss"),
            end: moment().format("YYYY-MM-DD HH:mm:ss")
          }
        },
        {
          label: "Yesterday",
          range: {
            start: moment().subtract(1, "days").format("YYYY-MM-DD HH:mm:ss"),
            end: moment().subtract(1, "days").format("YYYY-MM-DD HH:mm:ss")
          }
        },
        {
          label: "Last 7 days",
          range: {
            start: moment().subtract(7, "days").format("YYYY-MM-DD HH:mm:ss"),
            end: moment().format("YYYY-MM-DD HH:mm:ss")
          }
        },
        {
          label: "Last 14 days",
          range: {
            start: moment().subtract(14, "days").format("YYYY-MM-DD HH:mm:ss"),
            end: moment().format("YYYY-MM-DD HH:mm:ss")
          }
        },
        {
          label: "Custom range",
          range: null
        }
      ],
      minSearchRange: null,
      maxSearchRange: null
    }
  },
  computed: {
    ...mapGetters({
      students: "users/formListStudents",
      contactTracings: "contactTracing/contactTracings"
    }),
    fromDate() {
      return this.filterParams.dates ? this.filterParams.dates.start : null
    },
    toDate() {
      return this.filterParams.dates ? this.filterParams.dates.end : null
    },
    activeDataTableFields() {
      return this.dataTableFields.filter((field) =>
        this.showStudentNumber == true ? field : field.key !== "student_sis_id"
      )
    },
    maxDate() {
      return this.$helpers.maxSchoolYearDate()
    },
    minDate() {
      return this.$helpers.minSchoolYearDate()
    }
  },
  mounted() {
    this.initLazyLoading()
  },
  unmounted() {
    this.$store.commit("contactTracing/SET_CONTACT_TRACINGS", [])
  },
  methods: {
    updateStudentNumber(e) {
      this.showStudentNumber = e.target.checked
    },
    handleContactTracing(
      page,
      isExport,
      isLazyLoadingMode,
      isAdvancedSearch,
      noLoader = true
    ) {
      this.resetResponseMessages()
      this.isLoading = !noLoader
      if (this.filterParams.by_student) {
        this.pagination.activePage = page ? page : 1
        const params = {
          per_page: this.pagination.per_page.value,
          page: this.pagination.activePage
        }
        if (this.filterParams.search_query) {
          params.search_query = this.filterParams.search_query
        }
        if (this.filterParams.sort_query) {
          params.sort = this.filterParams.sort_query
        }
        if (this.filterParams.search_id) {
          params.search_id = this.filterParams.search_id
        }
        if (isAdvancedSearch || isExport) {
          params.user_id = this.filterParams.by_student.value.id
          params.from_date = this.fromDate
          params.to_date = this.toDate
        }
        if (isExport) {
          this.generateExport(isExport, params)
        } else {
          this.getContactTracings(params, isLazyLoadingMode, isAdvancedSearch)
        }
      } else {
        this.setErrorResponse("", {
          user_id: ["The user id field is required."]
        })
        this.$store.commit("contactTracing/SET_CONTACT_TRACINGS", [])
      }
    },
    getContactTracings(data, isLazyLoadingMode, isAdvancedSearch) {
      if (isAdvancedSearch) {
        this.isLoading = true
        this.$store
          .dispatch("contactTracing/getContactTracing", data)
          .then((response) => {
            const responseData = response.data.data
            if (responseData && responseData.search_id) {
              this.filterParams.search_id = responseData.search_id
              const params = { ...data, search_id: responseData.search_id }
              this.getContactTracingResults(params, isLazyLoadingMode)
            }
          })
          .catch(() => {
            this.isLoading = false
          })
      } else {
        if (this.filterParams.search_id) {
          this.getContactTracingResults(data, isLazyLoadingMode)
        }
      }
    },
    getContactTracingResults(params, isLazyLoadingMode) {
      this.isLoading = true
      this.$store
        .dispatch("contactTracing/getContactTracingSearchResults", params)
        .then((response) => {
          const data = response.data
          if (data.data) {
            if (isLazyLoadingMode) {
              this.$store.commit(
                "contactTracing/PUSH_CONTACT_TRACINGS",
                data.data
              )
            } else {
              this.$store.commit(
                "contactTracing/SET_CONTACT_TRACINGS",
                data.data
              )
            }
          }
          if (data.meta) {
            this.pagination.total = data.meta.total
            this.pagination.from = data.meta.from
            this.pagination.to = data.meta.to
            this.pagination.pages = Math.ceil(
              data.meta.total / data.meta.per_page
            )
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch((err) => {
          this.isLoading = false
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
        })
    },
    generateExport(type, params) {
      params.per_page = [25, 50, 100].includes(this.pagination.per_page.value)
        ? this.pagination.per_page.value
        : "all"
      const paramsData = {
        ...params,
        export_as: type == "excel" ? "print" : type,
        student_sis_id: this.showStudentNumber
      }
      if (type == "csv") {
        paramsData.search_id = this.filterParams.search_id
          ? this.filterParams.search_id
          : ""
      }
      if (type == "csv") {
        paramsData.search_id = this.filterParams.search_id
          ? this.filterParams.search_id
          : ""
      }
      this.$store
        .dispatch("contactTracing/exportContactTracing", paramsData)
        .then((response) => {
          if (response.data) {
            if (type == "print") {
              this.printContactTracings(response.data.data)
            } else {
              this.downloadExport(response.data, type)
            }
          }
        })
    },
    downloadExport(data, type) {
      const fileExtension = "csv"
      if (type === "excel") {
        this.excelExport(data)
        return
      }
      const csvDataForExport = "data:text/csv;charset=utf-8," + data
      const encodedUri = encodeURI(csvDataForExport)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("target", "_blank")
      link.setAttribute("download", "MeetUpReport." + fileExtension)
      document.body.appendChild(link)
      link.click()
    },
    excelExport(data) {
      if (data.data) {
        let table = document.getElementsByClassName("contactTracingDataTable")
        if (table && table[0]) {
          let tableHTML = "<table><thead>"
          const headers = `<tr>${
            this.showStudentNumber == true ? "<th>Student number</th>" : ""
          }<th>Last Name</th><th>First Name</th><th>Email</th><th>Number of times passes overlapped</th><th>Did they share a location?</th></tr>`
          table = table[0].cloneNode(true)
          tableHTML = tableHTML + headers + "</thead><tbody>"
          data.data.forEach((el) => {
            const studentNumber =
              this.showStudentNumber == true
                ? "<td>" + el.student_sis_id + "</td>"
                : ""
            tableHTML =
              tableHTML +
              `<tr>` +
              studentNumber +
              `<td>${el.last_name}</td><td>${el.first_name}</td><td>${
                el.email
              }</td><td>${el.overlap_time_count}</td><td>${
                el.share_same_location ? "Y" : "N"
              }</td></tr>`
          })
          table.innerHTML = tableHTML
          const workbook = XLSX.utils.table_to_book(table)
          const ws = workbook.Sheets["Sheet1"]
          XLSX.utils.sheet_add_aoa(
            ws,
            [["Created " + new Date().toISOString()]],
            { origin: -1 }
          )
          XLSX.writeFile(workbook, "MeetUpReport.xlsx")
        }
      }
    },
    clearFilter() {
      this.clearFilterParams()
      this.resetSearchQuery()
      this.handleContactTracing()
      this.resetResponseMessages()
    },
    advancedSearch() {
      this.resetSearchQuery()
      this.handleContactTracing(1, false, false, true)
    },
    clearFilterParams() {
      this.filterParams = {
        search_id: null,
        search_query: "",
        sort_query: "",
        by_student: null,
        dates: {
          start: moment().format("YYYY-MM-DD HH:mm:ss"),
          end: moment().format("YYYY-MM-DD HH:mm:ss")
        }
      }
      this.calendar.activeRange = this.optionalDateRanges[0]
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.handleContactTracing(this.pagination.activePage)
      this.$helpers.scrollToTop()
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.handleContactTracing()
      }
    },
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.contactTracings &&
            this.contactTracings.length &&
            this.pagination.total > this.contactTracings.length
          ) {
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.handleContactTracing(
                this.pagination.activePage,
                false,
                true,
                false,
                false
              )
            }
          }
        }
      }
    },
    showCalendarRanges(hidePopover) {
      if (this.calendar.isCalendarVisible) {
        hidePopover()
        this.handleDatePopoverShow()
      }
      this.calendar.isRangesVisible = true
      this.calendar.isCalendarVisible = false
    },
    setCalendarRange(range, togglePopover) {
      const isCustomRange = range && range.label == "Custom range"

      this.calendar.isCalendarVisible = isCustomRange
      this.calendar.isRangesVisible = false // !isCustomRange

      this.calendar.activeRange = range
      this.filterParams.dates = range.range
        ? range.range
        : this.filterParams.dates
      if (this.calendar.isCalendarVisible) {
        togglePopover()
      }
    },
    onContainerClick(e) {
      const target = e.target
      const classList =
        target.classList.contains("date-calendar-option") ||
        target.classList.contains("flaticon-calendar") ||
        target.classList.contains("field-icon")
      if (target && !classList) {
        this.calendar.isRangesVisible = false
      }
    },
    searchByQuery(value, type) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type)
      }, 500)
    },
    handleQuerySearch(value, type) {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.search_query = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  const searchValue =
                    col === "overlap_time_count"
                      ? columnData.value
                      : '"' + columnData.value + '"'
                  this.filterParams.search_query =
                    this.filterParams.search_query + `${col}:${searchValue}, `
                })
              }
            }
          }
          this.filterParams.search_query = this.filterParams.search_query.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.search_query = query ? '"' + query + '"' : query
        }
        // this.clearFilterParams();
        this.handleContactTracing()
      }
    },
    resetSearchQuery() {
      this.filterParams.search_query = ""
      this.columnSearchKeys = {
        globalQuery: "",
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        last_name: { value: "", column: ["last_name"], sortBy: "last_name" },
        first_name: { value: "", column: ["first_name"], sortBy: "first_name" },
        email: { value: "", column: ["email"], sortBy: "email" },
        overlap_time_count: {
          value: "",
          column: ["overlap_time_count"],
          sortBy: "overlap_time_count"
        },
        share_same_location: {
          value: "",
          column: ["share_same_location"],
          sortBy: "share_same_location"
        }
      }
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].sortBy
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      this.handleContactTracing()
    },
    printContactTracings(data) {
      //Using iframe
      let tableHTML =
        `<html><head><title></title></head><body class="p-2"><h1>` +
        document.title +
        `</h1><table class="print-table"><thead>`
      const headers = `<tr>${
        this.showStudentNumber == true ? "<th>Student number</th>" : ""
      }<th>Last Name</th><th>First Name</th><th>Email</th><th>Number of times passes overlapped</th><th>Did they share a location?</th></tr>`
      tableHTML = tableHTML + headers + "</thead><tbody>"
      data.forEach((el) => {
        tableHTML =
          tableHTML +
          `<tr>${
            this.showStudentNumber == true
              ? "<td>" + el.student_sis_id + "</td>"
              : ""
          }<td>${el.last_name}</td><td>${el.first_name}</td><td>${
            el.email
          }</td><td>${el.overlap_time_count}</td><td>${
            el.share_same_location ? "Y" : "N"
          }</td></tr>`
      })
      tableHTML =
        tableHTML +
        `</tbody></table><style>.print-table{border-spacing: 0px;}
                .print-table th, .print-table td{border: 1px solid #333;border-color: #333; border-spacing: 0px; padding: 5px;border-left-width: 0;}
                .print-table td{ border-top: none;},
                .print-table tr{border-spacing: 0px !important;} .print-table td:first-child, .print-table th:first-child {border-left-width: 1;};
          </style></body>`

      window.frames["print_frame"].document.body.innerHTML = tableHTML
      window.frames["print_frame"].window.focus()
      window.frames["print_frame"].window.print()

      return true
    },
    resetResponseMessages() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Someting went wrong!",
        errors: errors ? errors : {}
      }
    },
    setRangeValidation(date) {
      if (date) {
        this.maxSearchRange = moment(date.start)
          .add(15, "days")
          .format("YYYY-MM-DD HH:mm:ss")
        this.minSearchRange = moment(date.start).format("YYYY-MM-DD HH:mm:ss")
      }
    },
    handleDatePopoverShow() {
      this.maxSearchRange = null
      this.minSearchRange = null
    }
  }
}
</script>
